import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers, {
  StatisticsProps,
} from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import {
  COURSE_FIRST_FOLD,
  COURSE_NAMES,
  FAQ,
} from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { defineMessage, Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import ExpandableActivities from '@components/course-landing-page/expandable-activities'
import CourseQuizzes from '@components/course-landing-page/quizzes'
import ExploreTools, {
  ToolCardData,
} from '@components/course-landing-page/explore-tools'
import CodingalSubscription from '@components/home/codingal-subscription'
import CodingalProductBlog from '@components/blog/product-blogs'
import AboutUsCard from '@components/reviews-and-testimonials/about-us-card'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import HowClassWorks from '@components/course-landing-page/how-class-works'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { CODING_GRADE_6_8 } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['cgm'].trialCourseQuery}`

const STATISTICS: StatisticsProps[] = [
  {
    title: '🎓 1,000+',
    description: defineMessage({ message: 'Expert CS instructors' }),
    backgroundColor: 'bg-orange-200',
  },
  {
    title: '👨‍💻 7 years',
    description: defineMessage({ message: 'Average teaching experience' }),
    backgroundColor: 'bg-blue-50',
  },
  {
    title: '👩 91%',
    description: defineMessage({ message: 'Female faculty' }),
    backgroundColor: 'bg-yellow-100',
  },
  {
    title: '💻 500+',
    description: defineMessage({ message: 'Lessons and projects' }),
    backgroundColor: 'bg-green-50',
  },
]

const GRADE_6_8_TOOLS: ToolCardData[] = [
  {
    title: 'Python',
    description:
      'Write real Python code to build apps, tools and solve problems like a developer.',
    image: '/images/courses/coding-for-kids/python.jpg',
  },
  {
    title: 'Website Development',
    description:
      'Design and build real websites using HTML, CSS, Bootstrap and beginner JavaScript.',
    image: '/images/courses/coding-for-kids/website.jpg',
  },
  {
    title: 'MIT App Inventor',
    description:
      'Build fully functional mobile apps with features like GPS, camera and sensors.',
    image: '/images/courses/coding-for-kids/mit.jpg',
  },
  {
    title: 'AI Code 101',
    description:
      'Explore how AI works by training beginner machine learning models.',
    image: '/images/courses/coding-for-kids/ai-code-101.jpg',
  },
  {
    title: 'Data Science & ML',
    description:
      'Explore real datasets, spot patterns and discover how data powers smart technology.',
    image: '/images/courses/coding-for-kids/data-science-and-ml.jpg',
  },
  {
    title: 'Scratch',
    description:
      'Build block based projects used in select tracks to cement core logic before moving to Python.',
    image: '/images/courses/coding-for-kids/scratch.jpg',
  },
]

const CodingGrade68 = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }
  useStoreUTMParams()

  const seoConfig = formatSeoConfig({
    title: 'Online Coding Classes Grades 6-8 (Ages 11-14)',
    description:
      'Explore coding & AI with LIVE 1-on-1 online classes for Grades 6-8. Hands-on projects in Python, web dev, AI & more for ages 11-14. Try a free lesson today!',
    url: 'https://www.codingal.com/coding-for-kids/grade-6-8/',
    metaImage:
      'https://www.codingal.com/images/courses/coding-for-kids/grade-6-8/og.jpg',
  })

  return (
    <>
      <NextSeo {...seoConfig} />
      <FAQPageJsonLd
        mainEntity={FAQ[CODING_GRADE_6_8].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName={COURSE_FIRST_FOLD[CODING_GRADE_6_8].name}
        description={COURSE_FIRST_FOLD[CODING_GRADE_6_8].description}
        provider={{ '@type': 'Organization', name: 'Codingal' }}
        offers={{ '@type': 'Offer', category: 'Paid' }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT16H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={CODING_GRADE_6_8}
        urlQuery={urlQuery}
        isPlatformPage
        hideReadMore
        background="bg-white"
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={CODING_GRADE_6_8} />
          <RecommendedCourses
            courseCodes={['cgm', 'cp', 'cc', 'sp', 'gd', 'ad', 'rc', 'aigc']}
            title="Best coding classes for kids aged 11 to 14"
            description={
              <>
                Best coding classes for kids 11 to 14 Level up with Python, web
                development, AI, and hands-on projects that reflect real coding
                ability.
              </>
            }
            showAllCoursesButton
          />
          <ExploreTools
            title="What do 11 to 14 year olds explore in Codingal coding classes?"
            description="Middle schoolers dive into real programming languages, web development, and beginner AI through hands-on projects that connect to the world around them. As 6th, 7th, and 8th graders grow in confidence, they take on more advanced tools and increasingly ambitious builds."
            urlQuery={urlQuery}
            tools={GRADE_6_8_TOOLS}
            note="⭐ As students progress, they may also explore JavaScript, advanced Python, and beginner data science projects."
          />
          <CourseFeatures
            courseTitle={CODING_GRADE_6_8}
            title={
              <Trans>
                An engaging coding journey for kids in Grades 6 to 8
              </Trans>
            }
          />
          <Activities courseName={CODING_GRADE_6_8} urlQuery={urlQuery} />
          <Teachers
            urlQuery={urlQuery}
            title={<Trans>Learn from expert Computer Science teachers</Trans>}
            statistics={STATISTICS}
            description={
              <Trans>
                Codingal&apos;s Grade 6 to 8 teachers are trained Computer
                Science educators with 7 or more years of classroom experience.
                Every instructor completes a structured onboarding program
                before teaching, and is regularly evaluated by student and
                parent ratings.
                <br />
                Teachers are rated 4.9 out of 5 by parents and students. The
                Codingal curriculum is rated 4.8 out of 5. The company Net
                Promoter Score (NPS) is 86 as of Q1 2026.
              </Trans>
            }
          />
          <HowClassWorks courseName={CODING_GRADE_6_8} urlQuery={urlQuery} />
          <CodingForStudents
            title="Our coding courses for Grades 6 to 8 are designed for students ready to code at a deeper level"
            description="Our coding courses for Grades 6 to 8 are designed for students ready to code at a deeper level. Middle schoolers learn through real projects that connect coding to the world around them. Using Python, JavaScript, HTML, CSS, and MIT App Inventor, 6th, 7th, and 8th graders build functional websites, apps, and beginner AI projects in structured live Codingal classes. As young developers grow in ability, they also build stronger analytical thinking, creative problem-solving, and the confidence to design and build independently, skills that set them apart both inside and outside the classroom."
            imagePath="/images/courses/coding-for-kids/grade-6-8/coding"
            imageWidth={213}
            imageHeight={219}
            reverse
          />
          <ExpandableActivities
            title="100+ fun coding projects your child will love building"
            description="A guided coding journey where kids learn by creating, exploring, and building one step at a time."
            courseTitle={CODING_GRADE_6_8}
            urlQuery={urlQuery}
          />
          <CodingalSubscription compact subject="ELA" urlQuery={urlQuery} />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
            subject="coding"
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <StemAccredited
            urlQuery={urlQuery}
            heading="Unlock your skills and earn your coding certificate"
            description="In Codingal's Grades 6 to 8 track, students progress through structured modules that take them from beginner programming into real-world development with Python, JavaScript, and web technologies. Middle schoolers learn everything from core programming logic to data science fundamentals, app development, and beginner AI, building projects that reflect genuine technical ability. Upon completing the program, students earn a STEM.org accredited certificate, a recognized credential that strengthens academic portfolios and stands out in high school applications."
          />
          <CodingCertificate
            title="Our coding classes are built for middle schoolers ready to code at a deeper level"
            description="At Codingal, our Grades 6 to 8 curriculum takes students from foundational programming concepts to building functional websites, apps, and data projects. We begin with structured challenges that introduce Python, HTML, and CSS through hands-on, project-based learning. As 6th, 7th, and 8th graders advance, they explore JavaScript, data science, and beginner AI in a way that connects coding to the world around them. With every project they complete, young developers build stronger analytical thinking and the confidence to design and build independently."
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our coding classes are built for middle schoolers ready to code at a deeper level"
            description="At Codingal, our Grades 6 to 8 curriculum takes students from foundational programming concepts to building functional websites, apps, and data projects. We begin with structured challenges that introduce Python, HTML, and CSS through hands-on, project-based learning designed to connect coding to the real world. As 6th, 7th, and 8th graders advance, they explore JavaScript, data science, and beginner AI at a level that builds genuine technical confidence. With every project they complete, young developers strengthen their analytical thinking and grow more capable of designing and building independently."
            imagePath="/images/courses/ai-and-ml/ai-course-students"
            imageWidth={235}
            imageHeight={241}
          />
          <Investors isCoursePage urlQuery={urlQuery} />
          <Faq
            courseName={CODING_GRADE_6_8}
            title="FAQs for Coding Classes for Grades 6 to 8"
          />
          <CourseQuizzes courseName={CODING_GRADE_6_8} />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            category="coding-for-kids"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={CODING_GRADE_6_8}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <AboutUsCard />
      <FooterBanner
        title="AI & coding classes for Grade 6-8 kids with the best AI & coding teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return { props: {} }
}

export default CodingGrade68
